import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import UniversalAddressInput from './UniversalAddressInput';
import { 
  X, 
  AlertTriangle, 
  Save, 
  MapPin, 
  User, 
  DollarSign, 
  Plus, 
  Trash2,
  Lock,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UnifiedEditJobModal = ({ job, isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(true);
  const [formData, setFormData] = useState({
    // Quick Post Fields
    title: '',
    industry: ['Tyre Service'],
    job_specification: '',
    location: '',
    quoted_price: '',
    urgency_level: 'normal',
    
    // Detailed Information Fields
    detailed_location: '',
    location_name: '',
    gps_coordinates: { lat: '', lng: '' },
    client_name: '',
    client_contact_name: '',
    client_contact_phone: '',
    client_contact_email: '',
    pricing_breakdown: [{ item: '', quantity: 1, price: 0 }],
    brand_requirements: '',
    alternate_brand_offer: '',
    special_notes: '',
    
    // Invoice tracking fields
    originator_invoice_number: '',
    receiver_invoice_number: ''
  });

  useEffect(() => {
    if (job && isOpen) {
      // Ensure fresh data is loaded with comprehensive null-safe handling
      setFormData({
        // Quick Post Fields
        title: job.title || '',
        industry: job.industry || ['Tyre Service'],
        job_specification: job.job_specification || job.tyre_specification || '',
        location: job.location || '',
        quoted_price: job.quoted_price || '',
        urgency_level: job.urgency_level || 'normal',
        
        // Detailed Information Fields - Enhanced null-safe handling
        detailed_location: job.detailed_location || '',
        location_name: job.location_name || '',
        gps_coordinates: job.gps_coordinates || { lat: '', lng: '' },
        client_name: job.client_name || '',
        client_contact_name: job.client_contact_name || '',
        client_contact_phone: job.client_contact_phone || '',
        client_contact_email: job.client_contact_email || '',
        
        // CRITICAL: Preserve existing pricing breakdown (prevent data loss)
        pricing_breakdown: (job.pricing_breakdown && Array.isArray(job.pricing_breakdown) && job.pricing_breakdown.length > 0) 
          ? job.pricing_breakdown.map(item => ({
              item: item.item || '',
              quantity: item.quantity || 1,
              price: item.price || 0
            }))
          : [{ item: '', quantity: 1, price: 0 }],
          
        brand_requirements: job.brand_requirements || '',
        alternate_brand_offer: job.alternate_brand_offer || '',
        special_notes: job.special_notes || '',
        
        // Invoice tracking fields
        originator_invoice_number: job.originator_invoice_number || '',
        receiver_invoice_number: job.receiver_invoice_number || ''
      });
      setShowWarning(true);
    }
  }, [job, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIndustryChange = (industry) => {
    setFormData(prev => ({
      ...prev,
      industry: prev.industry.includes(industry)
        ? prev.industry.filter(i => i !== industry)
        : [...prev.industry, industry]
    }));
  };

  const handleGpsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      gps_coordinates: {
        ...prev.gps_coordinates,
        [field]: value
      }
    }));
  };

  const handlePricingChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      pricing_breakdown: prev.pricing_breakdown.map((item, i) => 
        i === index ? { 
          ...item, 
          [field]: field === 'price' || field === 'quantity' ? parseFloat(value) || 0 : value 
        } : item
      )
    }));
  };

  const addPricingItem = () => {
    setFormData(prev => ({
      ...prev,
      pricing_breakdown: [...prev.pricing_breakdown, { item: '', quantity: 1, price: 0 }]
    }));
  };

  const removePricingItem = (index) => {
    setFormData(prev => ({
      ...prev,
      pricing_breakdown: prev.pricing_breakdown.filter((_, i) => i !== index)
    }));
  };

  const hasChanges = () => {
    if (!job) return false;
    
    // Check Quick Post fields
    const quickChanges = (
      formData.title !== job.title ||
      JSON.stringify(formData.industry.sort()) !== JSON.stringify((job.industry || ['Tyre Service']).sort()) ||
      formData.job_specification !== (job.job_specification || job.tyre_specification || '') ||
      formData.location !== job.location ||
      parseFloat(formData.quoted_price) !== job.quoted_price ||
      formData.urgency_level !== job.urgency_level
    );

    // Check Detailed fields
    const detailedChanges = (
      formData.detailed_location !== (job.detailed_location || '') ||
      formData.location_name !== (job.location_name || '') ||
      JSON.stringify(formData.gps_coordinates) !== JSON.stringify(job.gps_coordinates || { lat: '', lng: '' }) ||
      formData.client_name !== (job.client_name || '') ||
      formData.client_contact_name !== (job.client_contact_name || '') ||
      formData.client_contact_phone !== (job.client_contact_phone || '') ||
      formData.client_contact_email !== (job.client_contact_email || '') ||
      JSON.stringify(formData.pricing_breakdown) !== JSON.stringify(job.pricing_breakdown || [{ item: '', quantity: 1, price: 0 }]) ||
      formData.brand_requirements !== (job.brand_requirements || '') ||
      formData.alternate_brand_offer !== (job.alternate_brand_offer || '') ||
      formData.special_notes !== (job.special_notes || '') ||
      formData.originator_invoice_number !== (job.originator_invoice_number || '') ||
      formData.receiver_invoice_number !== (job.receiver_invoice_number || '')
    );

    return quickChanges || detailedChanges;
  };

  const hasPriceChange = () => {
    return job && parseFloat(formData.quoted_price) !== job.quoted_price;
  };

  const getChangedFields = () => {
    if (!job) return [];
    
    const changes = [];
    
    // Quick Post field changes
    if (formData.title !== job.title) changes.push('title');
    if (JSON.stringify(formData.industry.sort()) !== JSON.stringify((job.industry || ['Tyre Service']).sort())) changes.push('industry');
    if (formData.job_specification !== (job.job_specification || job.tyre_specification || '')) changes.push('specification');
    if (formData.location !== job.location) changes.push('location');
    if (parseFloat(formData.quoted_price) !== job.quoted_price) changes.push('price');
    if (formData.urgency_level !== job.urgency_level) changes.push('urgency');
    
    // Detailed field changes
    if (formData.detailed_location !== (job.detailed_location || '')) changes.push('detailed location');
    if (formData.location_name !== (job.location_name || '')) changes.push('location name');
    if (JSON.stringify(formData.gps_coordinates) !== JSON.stringify(job.gps_coordinates || { lat: '', lng: '' })) changes.push('GPS coordinates');
    if (formData.client_name !== (job.client_name || '')) changes.push('client info');
    if (formData.client_contact_name !== (job.client_contact_name || '')) changes.push('client contact');
    if (formData.client_contact_phone !== (job.client_contact_phone || '')) changes.push('client phone');
    if (formData.client_contact_email !== (job.client_contact_email || '')) changes.push('client email');
    if (JSON.stringify(formData.pricing_breakdown) !== JSON.stringify(job.pricing_breakdown || [{ item: '', quantity: 1, price: 0 }])) changes.push('pricing breakdown');
    if (formData.brand_requirements !== (job.brand_requirements || '')) changes.push('brand requirements');
    if (formData.alternate_brand_offer !== (job.alternate_brand_offer || '')) changes.push('alternate brands');
    if (formData.special_notes !== (job.special_notes || '')) changes.push('special notes');
    if (formData.originator_invoice_number !== (job.originator_invoice_number || '')) changes.push('originator invoice');
    if (formData.receiver_invoice_number !== (job.receiver_invoice_number || '')) changes.push('receiver invoice');
    
    return changes;
  };

  const isLocked = () => {
    return job && (job.status === 'accepted' || job.status === 'completed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasChanges()) {
      toast.info('No changes detected');
      return;
    }

    if (isLocked()) {
      toast.error('Job is locked and cannot be edited');
      return;
    }

    setLoading(true);

    try {
      // PATCH semantics - only send changed fields
      const jobUpdateData = {};
      
      // Check each field and only include if changed
      if (formData.title !== (job.title || '')) {
        jobUpdateData.title = formData.title;
      }
      if (JSON.stringify(formData.industry.sort()) !== JSON.stringify((job.industry || ['Tyre Service']).sort())) {
        jobUpdateData.industry = formData.industry;
      }
      if (formData.job_specification !== (job.job_specification || job.tyre_specification || '')) {
        jobUpdateData.job_specification = formData.job_specification;
      }
      if (formData.location !== (job.location || '')) {
        jobUpdateData.location = formData.location;
      }
      if (parseFloat(formData.quoted_price) !== (job.quoted_price || 0)) {
        jobUpdateData.quoted_price = parseFloat(formData.quoted_price);
      }
      if (formData.urgency_level !== (job.urgency_level || 'normal')) {
        jobUpdateData.urgency_level = formData.urgency_level;
      }
      
      // Detailed Information Fields - only include if changed
      if (formData.detailed_location !== (job.detailed_location || '')) {
        jobUpdateData.detailed_location = formData.detailed_location || null;
      }
      if (formData.location_name !== (job.location_name || '')) {
        jobUpdateData.location_name = formData.location_name || null;
      }
      if (JSON.stringify(formData.gps_coordinates) !== JSON.stringify(job.gps_coordinates || { lat: '', lng: '' })) {
        jobUpdateData.gps_coordinates = (formData.gps_coordinates.lat && formData.gps_coordinates.lng) 
          ? formData.gps_coordinates 
          : null;
      }
      if (formData.client_name !== (job.client_name || '')) {
        jobUpdateData.client_name = formData.client_name || null;
      }
      if (formData.client_contact_name !== (job.client_contact_name || '')) {
        jobUpdateData.client_contact_name = formData.client_contact_name || null;
      }
      if (formData.client_contact_phone !== (job.client_contact_phone || '')) {
        jobUpdateData.client_contact_phone = formData.client_contact_phone || null;
      }
      if (formData.client_contact_email !== (job.client_contact_email || '')) {
        jobUpdateData.client_contact_email = formData.client_contact_email || null;
      }
      
      // Pricing breakdown - only update if explicitly changed
      const currentBreakdown = job.pricing_breakdown || [];
      const newBreakdown = formData.pricing_breakdown.filter(item => 
        item.item.trim() !== '' || item.quantity > 0 || item.price > 0
      );
      
      if (JSON.stringify(currentBreakdown) !== JSON.stringify(newBreakdown)) {
        jobUpdateData.pricing_breakdown = newBreakdown;
      }
      
      if (formData.brand_requirements !== (job.brand_requirements || '')) {
        jobUpdateData.brand_requirements = formData.brand_requirements || null;
      }
      if (formData.alternate_brand_offer !== (job.alternate_brand_offer || '')) {
        jobUpdateData.alternate_brand_offer = formData.alternate_brand_offer || null;
      }
      if (formData.special_notes !== (job.special_notes || '')) {
        jobUpdateData.special_notes = formData.special_notes || null;
      }
      
      // Handle invoice fields separately if there are any changes
      const invoiceData = {};
      if (user.id === job.originator_id && formData.originator_invoice_number !== (job.originator_invoice_number || '')) {
        invoiceData.originator_invoice_number = formData.originator_invoice_number || null;
      }
      if (user.id === job.accepted_by && formData.receiver_invoice_number !== (job.receiver_invoice_number || '')) {
        invoiceData.receiver_invoice_number = formData.receiver_invoice_number || null;
      }

      let response;
      
      // Update job details if there are changes (PATCH semantics)
      if (Object.keys(jobUpdateData).length > 0) {
        response = await axios.put(`${API}/jobs/${job.id}`, jobUpdateData);
        
        // Check if funding was invalidated
        if (response.data.funding_invalidated) {
          toast.warning('Funding was voided due to price/breakdown changes. Job will need to be re-funded.');
        }
      }
      
      // Update invoice fields separately if there are any
      if (Object.keys(invoiceData).length > 0) {
        await axios.put(`${API}/jobs/${job.id}/invoice`, invoiceData);
      }
      
      if (Object.keys(jobUpdateData).length > 0 || Object.keys(invoiceData).length > 0) {
        toast.success('Job updated successfully!');
        
        if (response?.data?.notifications_sent) {
          toast.info('Receivers have been notified of the changes via SMS');
        }
      } else {
        toast.info('No changes detected');
      }
      
      onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Failed to update job:', error);
      const message = error.response?.data?.detail || 'Failed to update job';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const industryOptions = [
    'Tyre Service',
    'Mechanics', 
    'Haulage',
    'Plumbing',
    'Electrical',
    'Other'
  ];

  const totalPrice = formData.pricing_breakdown.reduce((sum, item) => 
    sum + (item.quantity * item.price), 0
  );

  if (!isOpen || !job) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] overflow-auto">
        {isLocked() ? (
          // Locked View
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Job Details</h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <Lock className="h-5 w-5 text-gray-600 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-800">
                    {job.status === 'accepted' ? 'Job Locked After Acceptance' : 'Job Permanently Locked'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {job.status === 'accepted' 
                      ? 'Use Pass-Back if the job can\'t be completed as specified.'
                      : 'Completed jobs cannot be modified.'
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <button onClick={onClose} className="jop-button-secondary">
                Close
              </button>
            </div>
          </div>
        ) : showWarning ? (
          // Warning Step
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Confirm Update</h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-start p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-amber-800 mb-2">
                    Changing job details will re-notify receivers
                  </h3>
                  <p className="text-sm text-amber-700 mb-3">
                    Updates to key details (price, specs, location, urgency) will trigger SMS alerts to all on-call receivers.
                  </p>
                  
                  {hasPriceChange() && (
                    <div className="p-3 bg-amber-100 border border-amber-300 rounded">
                      <p className="text-sm font-medium text-amber-800">
                        💰 Commission will be recalculated based on the new price.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {hasChanges() && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Changes to be made:</h4>
                <div className="max-h-32 overflow-y-auto">
                  <ul className="text-sm text-gray-600 space-y-1">
                    {getChangedFields().map(field => (
                      <li key={field} className="flex items-center">
                        <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                        {field.charAt(0).toUpperCase() + field.slice(1)}
                        {field === 'price' && ` (${job.quoted_price} → ${formData.quoted_price})`}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={() => setShowWarning(false)}
                className="jop-button flex-1"
              >
                Proceed with Edit
              </button>
              <button
                onClick={onClose}
                className="jop-button-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          // Unified Edit Form
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Edit Job Details</h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Quick Post Section */}
              <div className="space-y-6">
                <div className="border-l-4 border-amber-500 pl-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Job Information</h3>
                  <p className="text-sm text-gray-600 mb-4">Changes here will trigger SMS notifications to receivers</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="jop-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="jop-input"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {industryOptions.map((industry) => (
                      <label key={industry} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.industry.includes(industry)}
                          onChange={() => handleIndustryChange(industry)}
                          className="rounded border-gray-300 text-amber-600 shadow-sm focus:border-amber-500 focus:ring-amber-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{industry}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Specification *
                  </label>
                  <textarea
                    name="job_specification"
                    value={formData.job_specification}
                    onChange={handleChange}
                    rows="3"
                    className="jop-input resize-none"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quoted Price ($) *
                    </label>
                    <input
                      type="number"
                      name="quoted_price"
                      value={formData.quoted_price}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      className="jop-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Urgency *
                    </label>
                    <select
                      name="urgency_level"
                      value={formData.urgency_level}
                      onChange={handleChange}
                      className="jop-select"
                      required
                    >
                      <option value="normal">Normal - Next 24-48 hours</option>
                      <option value="asap">ASAP - Within 4 hours</option>
                      <option value="after_hours">After Hours - Emergency</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Detailed Information Section */}
              <div className="space-y-6">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Detailed Information</h3>
                  <p className="text-sm text-gray-600 mb-4">Private details visible only to assigned receiver</p>
                </div>

                {/* Location Details */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900 flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                    Precise Location
                  </h4>
                  
                  <UniversalAddressInput
                    value={formData.detailed_location}
                    onChange={(value) => handleChange({ target: { name: 'detailed_location', value } })}
                    onAddressSelect={(addressData) => {
                      setFormData(prev => ({
                        ...prev,
                        detailed_formatted_address: addressData.formatted_address,
                        detailed_lat: addressData.lat,
                        detailed_lng: addressData.lng,
                        detailed_place_id: addressData.place_id,
                        detailed_manual_entry: addressData.manual_entry
                      }));
                    }}
                    placeholder="123 Main Street, Newcastle NSW 2300"
                    label="Full Address"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location Name (Business/Landmark)
                    </label>
                    <input
                      type="text"
                      name="location_name"
                      value={formData.location_name}
                      onChange={handleChange}
                      className="jop-input"
                      placeholder="e.g., Newcastle Tyre Service Center"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GPS Coordinates (Latitude, Longitude)
                    </label>
                    <input
                      type="text"
                      value={`${formData.gps_coordinates.lat || ''}${formData.gps_coordinates.lat && formData.gps_coordinates.lng ? ', ' : ''}${formData.gps_coordinates.lng || ''}`}
                      onChange={(e) => {
                        const coords = e.target.value.split(',').map(s => s.trim());
                        const lat = coords[0] || '';
                        const lng = coords[1] || '';
                        setFormData(prev => ({
                          ...prev,
                          gps_coordinates: { lat, lng }
                        }));
                      }}
                      className="jop-input"
                      placeholder="-32.9267, 151.7789"
                    />
                  </div>
                </div>

                {/* Client Information */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900 flex items-center">
                    <User className="h-4 w-4 mr-2 text-gray-400" />
                    Client Information
                  </h4>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-xs text-blue-700">
                        Client details remain private until job is accepted
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Client/Company Name
                      </label>
                      <input
                        type="text"
                        name="client_name"
                        value={formData.client_name}
                        onChange={handleChange}
                        className="jop-input"
                        placeholder="ABC Transport"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Person
                      </label>
                      <input
                        type="text"
                        name="client_contact_name"
                        value={formData.client_contact_name}
                        onChange={handleChange}
                        className="jop-input"
                        placeholder="John Smith"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Phone
                      </label>
                      <input
                        type="tel"
                        name="client_contact_phone"
                        value={formData.client_contact_phone}
                        onChange={handleChange}
                        className="jop-input"
                        placeholder="+61 400 000 000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        name="client_contact_email"
                        value={formData.client_contact_email}
                        onChange={handleChange}
                        className="jop-input"
                        placeholder="contact@client.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Pricing Breakdown */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900 flex items-center">
                    <DollarSign className="h-4 w-4 mr-2 text-gray-400" />
                    Pricing Breakdown
                  </h4>

                  <div className="space-y-3">
                    {formData.pricing_breakdown.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-3 items-end">
                        <div className="col-span-5">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Item/Service
                          </label>
                          <input
                            type="text"
                            value={item.item}
                            onChange={(e) => handlePricingChange(index, 'item', e.target.value)}
                            className="jop-input"
                            placeholder="e.g., Tyre 235/75R15"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Qty
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handlePricingChange(index, 'quantity', e.target.value)}
                            className="jop-input"
                          />
                        </div>
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Unit Price ($)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.price}
                            onChange={(e) => handlePricingChange(index, 'price', e.target.value)}
                            className="jop-input"
                          />
                        </div>
                        <div className="col-span-2 flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            ${(item.quantity * item.price).toFixed(2)}
                          </span>
                          {formData.pricing_breakdown.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removePricingItem(index)}
                              className="text-red-600 hover:text-red-800 p-1"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={addPricingItem}
                      className="flex items-center text-sm text-amber-600 hover:text-amber-700"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </button>
                    <div className="text-lg font-semibold text-gray-900">
                      Total: ${totalPrice.toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Brand & Special Requirements */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Brand Specific Requirements
                    </label>
                    <textarea
                      name="brand_requirements"
                      value={formData.brand_requirements}
                      onChange={handleChange}
                      rows="3"
                      className="jop-input resize-none"
                      placeholder="e.g., Must use Michelin or equivalent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alternate Brand Offer
                    </label>
                    <textarea
                      name="alternate_brand_offer"
                      value={formData.alternate_brand_offer}
                      onChange={handleChange}
                      rows="3"
                      className="jop-input resize-none"
                      placeholder="e.g., Can offer Bridgestone at $50 discount"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Special Notes & Instructions
                  </label>
                  <textarea
                    name="special_notes"
                    value={formData.special_notes}
                    onChange={handleChange}
                    rows="4"
                    className="jop-input resize-none"
                    placeholder="Any special requirements, access instructions, timing constraints, etc."
                  />
                </div>

                {/* Invoice Tracking - Role-based access */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900 flex items-center">
                    <DollarSign className="h-4 w-4 mr-2 text-gray-400" />
                    Invoice Tracking
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Originator Invoice - Only for job originator */}
                    {user.id === job.originator_id && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Originator Invoice Number
                        </label>
                        <input
                          type="text"
                          name="originator_invoice_number"
                          value={formData.originator_invoice_number}
                          onChange={handleChange}
                          className="jop-input"
                          placeholder="e.g., INV-2025-001"
                        />
                      </div>
                    )}
                    
                    {/* Receiver Invoice - Only for assigned receiver */}
                    {user.id === job.accepted_by && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Receiver Invoice Number
                        </label>
                        <input
                          type="text"
                          name="receiver_invoice_number"
                          value={formData.receiver_invoice_number}
                          onChange={handleChange}
                          className="jop-input"
                          placeholder="e.g., REC-2025-001"
                        />
                      </div>
                    )}
                    
                    {/* Show read-only info for users who can't edit */}
                    {user.id !== job.originator_id && user.id !== job.accepted_by && (
                      <div className="md:col-span-2">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <p className="text-xs text-gray-600">
                            Invoice numbers can only be edited by the originator or assigned receiver
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading || !hasChanges()}
                  className="jop-button flex-1 flex items-center justify-center"
                >
                  {loading ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save All Changes
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowWarning(true)}
                  className="jop-button-secondary"
                >
                  Back
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedEditJobModal;